/**
 * Calls the agent's `chat/invoke` endpoint, which bootstraps a thread with its settings and
 * triggers the agent's opening turn — used by the chat page to have Bestie speak first.
 *
 * `force_skills` alone is a valid prompt for the endpoint. `first_message` is deliberately not
 * sent: it seeds a canned opening line and skips the agent graph, so the skill would never run.
 *
 * The route is allowlisted with `requiresAuth: true`, so an anonymous caller gets a 401 from our
 * own proxy and never reaches the agent.
 */
export interface ChatInvokeResponse {
  thread_id?: string | null;
  content: string;
  /** Same shape as `ChatMessage['structured_content']`, so it feeds
   *  `mapStructuredContentToMetadata` directly with no cast. */
  structured_content?: Record<string, unknown> | unknown[] | string | null;
  error?: string | null;
}

export const invokeChatWithSkills = async (
  { threadId, skills }: { threadId: string; skills: string[] },
): Promise<ChatInvokeResponse> => {
  const abortController = new AbortController();
  // 30s, vs. `fetchHistory`'s 10s (`useStreamStateV2.tsx`): that call is a plain read, this one
  // is non-streaming but runs a full LLM turn plus a tool call (the skill's job search), which
  // routinely takes longer. While this is pending, `isGreetingPending` disables the prompt bar,
  // so the bound exists to keep the chat page merely slow rather than unusable if the agent hangs.
  const timeoutId = setTimeout(() => abortController.abort(), 30000);

  try {
    const response = await fetch('/api/agent/chat/invoke', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        thread_id: threadId,
        prompt: { force_skills: skills },
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      // Include the body so a 500 from the agent is diagnosable from the console — this failure
      // is deliberately silent to the user (falls back to the canned landing) but must not also
      // be silent to whoever is debugging it.
      const bodyText = await response.text().catch(() => '');
      throw new Error(`chat/invoke failed: ${response.status}${bodyText ? ` — ${bodyText}` : ''}`);
    }

    const data = await response.json() as ChatInvokeResponse;

    if (data.error) {
      throw new Error(`chat/invoke returned an error: ${data.error}`);
    }

    return data;
  } finally {
    clearTimeout(timeoutId);
  }
};
